/**
 * Firebase Cloud Messaging helpers
 * - requestNotificationPermission  : ask user, save token to Firestore
 * - onForegroundMessage            : subscribe to in-app messages
 * - sendNotification               : POST to /api/notify (Vercel function)
 */
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import app, { db } from './firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Lazy singleton
let _messaging = null;
function getMsg() {
  if (_messaging) return _messaging;
  try { _messaging = getMessaging(app); } catch { _messaging = null; }
  return _messaging;
}

// ── Request permission + register token ──────────────────────
export async function requestNotificationPermission(userId) {
  if (!('Notification' in window)) return null;
  if (!VAPID_KEY) {
    console.warn('[FCM] REACT_APP_FIREBASE_VAPID_KEY not set');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  try {
    const messaging = getMsg();
    if (!messaging) return null;

    // Register service worker first
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    if (!token) return null;

    // Persist token → Firestore fcm_tokens/{token} (token as doc ID supports multiple devices per user)
    await setDoc(doc(db, 'fcm_tokens', token), {
      token,
      platform: 'web',
      userId,
      updatedAt: serverTimestamp(),
    });

    console.log('[FCM] Token registered for', userId);
    return token;
  } catch (err) {
    console.error('[FCM] token error:', err);
    return null;
  }
}

// ── Foreground message listener ───────────────────────────────
export function onForegroundMessage(callback) {
  const messaging = getMsg();
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}

// ── Send notification via Vercel API route ────────────────────
// targetUserIds: 'all'  → broadcast to everyone
//               ['game','benz'] → only those users
export async function sendNotification({ type, title, body, targetUserIds = 'all', data = {} }) {
  try {
    const res = await fetch('/api/notify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-notify-secret': process.env.REACT_APP_NOTIFY_SECRET || '',
      },
      body: JSON.stringify({ type, title, body, targetUserIds, data: { ...data, type } }),
    });
    if (!res.ok) console.warn('[FCM] notify failed:', await res.text());
    return res.ok;
  } catch (err) {
    console.error('[FCM] sendNotification error:', err);
    return false;
  }
}
